export type ModuleActivation = 'IMMEDIATE' | 'ON_PAYMENT';
export type PaymentStatus = 'UNPAID' | 'PAID';
export type ApprovalStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface PlanSelection {
  kind: 'FIXED' | 'FLEXIBLE';
  planCode: string | null;
  modules: string[];
  billingPeriod: 'MONTHLY' | 'YEARLY';
}

export interface BillingModule {
  code: string;
  featureFlag: string | null;
  name: string;
  monthlyPrice: number | null;
  yearlyPrice: number | null;
  defaultLimit: number | null;
  activation: ModuleActivation;
  active: boolean;
  sortOrder: number;
}

export interface PlanModule {
  featureFlag: string;
  monthlyLimit: number | null;
}

export interface Plan {
  code: string;
  name: string;
  monthlyPrice: number | null;
  yearlyPrice: number | null;
  active: boolean;
  sortOrder: number;
  modules: PlanModule[];
}

export interface SubscribedModule {
  featureFlag: string;
  monthlyLimit: number | null;
  activation: ModuleActivation;
  live: boolean;
}

export interface Subscription {
  id: number;
  tenantId: number;
  tenantName: string | null;
  kind: 'FIXED' | 'FLEXIBLE';
  planCode: string | null;
  planName: string;
  billingPeriod: 'MONTHLY' | 'YEARLY';
  price: number;
  periodStart: string;
  periodEnd: string;
  paymentStatus: PaymentStatus;
  paidAt: string | null;
  selfService: boolean;
  approvalStatus: ApprovalStatus;
  overdue: boolean;
  renewalDue: boolean;
  /** An earlier period, listed because it is still unpaid. */
  pastPeriod: boolean;
  modules: SubscribedModule[];
}

/** What each module does, for whoever is deciding what to sell it for. */
export const MODULE_DESCRIPTIONS: Record<string, string> = {
  FEATURE_USERS: 'Adding and managing colleagues\' accounts. The limit is how many people the lab can have.',
  FEATURE_PHASES: 'Maintaining phases, recording them on a case, and the phase report.',
  FEATURE_STOCK: 'Stocks, stock documents and material options on products. The limit is how many active stocks.',
  FEATURE_STOCK_MIN: 'Issuing material from the warehouse.',
  FEATURE_FINANCE: 'Product catalogue, invoicing and the finance dashboard.',
  FEATURE_WAGES: 'Wage settings and monthly worker payments.',
  FEATURE_COURIER: 'Shipments delivered by courier.',
  FEATURE_API: 'Connecting the lab\'s own systems with API keys.',
  FEATURE_SMS: 'Text messages to recipients when a shipment is sent. The limit is the SMS credit per month, and a long message uses more than one; each one costs money.',
  FEATURE_SUPPORT_EMAIL: 'What the lab is promised: we answer emails within this many hours. Nothing in the app enforces it.',
  FEATURE_SUPPORT_CHAT: 'What the lab is promised: support over instant messaging, best effort. Nothing in the app enforces it.',
  FEATURE_STORAGE: 'File storage. The limit is a total in GB, not per month; 1 TB is 1024. Without this module a lab gets the free figure.',
  FEATURE_CHAT_BOT: 'Drafts cases and plans from speech or text. The limit is requests per month; each one costs money.',
  FEATURE_VOICE_MESSAGES: 'Voice notes in chats with a transcript. The limit is minutes per month; each one costs money.'
};

/** The modules that carry a figure, and what it counts. */
export const MODULE_LIMIT_UNITS: Record<string, string> = {
  FEATURE_USERS: 'users',
  FEATURE_STOCK: 'active stocks',
  FEATURE_SMS: 'SMS credits / month',
  FEATURE_STORAGE: 'GB total',
  FEATURE_SUPPORT_EMAIL: 'hours to respond',
  FEATURE_CHAT_BOT: 'requests / month',
  FEATURE_VOICE_MESSAGES: 'minutes / month'
};
